// ── AdminDataAssets — 관리자: 데이터 자산 인벤토리 시각화 ────────
// 본인 매장의 데이터가 어떻게 쌓이고 있는지 + 자산화 활용 방안 표시
// 향후 익명 집계 컬렉션 (anonymizedStats/) 추가 시 그 데이터도 표시
import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, getDocs, query, where, limit, orderBy,
} from 'firebase/firestore';
import { db } from '../db';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const fmtWon = n => fmt(n) + '원';

export default function AdminDataAssets({ user }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const out = await collectStats(user.uid);
        if (mounted) setStats(out);
      } catch (e) {
        console.error(e);
        if (mounted) setError(e.message || '데이터 로드 실패');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>⏳ 데이터 수집 중...</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: '#52525B' }}>오류: {error}</div>;
  }
  if (!stats) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* 헤더 */}
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a1a' }}>
          📊 본인 매장 데이터 자산 인벤토리
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.6 }}>
          현재 본인 매장에 쌓이고 있는 데이터입니다. 향후 익명 집계 + 약관 동의 후 시장 인텔리전스로 활용.
        </div>
      </div>

      {/* 데이터 카드 그리드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 12,
      }}>
        <DataCard
          icon="🧾" title="거래장부 (transactions)"
          mainStat={stats.transactionCount}
          mainLabel="총 거래"
          subStats={[
            { label: '매출', val: stats.incomeCount, color: '#18181B' },
            { label: '매입', val: stats.expenseCount, color: '#EA580C' },
            { label: '이번달', val: stats.thisMonthTransactions, color: '#1A56A0' },
          ]}
          assetValue="⭐⭐⭐⭐⭐"
          useCase="① 식재료 시장가 ② 매장 운영 벤치마크 ③ 거래 트렌드"
        />

        <DataCard
          icon="📸" title="영수증 OCR 누적"
          mainStat={stats.ocrReceiptCount}
          mainLabel="OCR 영수증"
          subStats={[
            { label: '총 lineItems', val: stats.totalLineItems, color: '#374151' },
            { label: '이번달', val: stats.thisMonthReceipts, color: '#1A56A0' },
          ]}
          assetValue="⭐⭐⭐⭐⭐"
          useCase="식재료 단가 인텔리전스의 원천 데이터. 매장 식별 제거 후 평균/최저가 집계"
        />

        <DataCard
          icon="🍽️" title="메뉴 판매 데이터"
          mainStat={stats.menuSalesLineItems}
          mainLabel="메뉴 판매 라인"
          subStats={[
            { label: '총 판매 수량', val: stats.totalMenuQty, color: '#18181B' },
            { label: '평균 단가', val: fmtWon(stats.avgMenuPrice), color: '#EA580C', isString: true },
          ]}
          assetValue="⭐⭐⭐⭐"
          useCase="외식업 메뉴 트렌드 · 시즌별 인기 메뉴 · vendor B2B 리포트"
        />

        <DataCard
          icon="📋" title="간이영수증 메모 (purchaseLog)"
          mainStat={stats.purchaseLogCount}
          mainLabel="누적 메모 영수증"
          subStats={[
            { label: '총 lineItems', val: stats.purchaseLogItems, color: '#374151' },
            { label: '면세 라인', val: stats.purchaseLogTaxFree, color: '#854d0e' },
          ]}
          assetValue="⭐⭐⭐⭐"
          useCase="간이영수증 = 적격증빙 X → 거래장부 미입력. 품목·단가 데이터만 자산화. 정산 시 세금계산서로 별도 입력."
        />

        <DataCard
          icon="🏷️" title="단가표 (prices)"
          mainStat={stats.priceCount}
          mainLabel="등록 식재료"
          subStats={[
            { label: 'AI 매칭됨', val: stats.priceMatched, color: '#18181B' },
            { label: 'itemPrices 누적', val: stats.itemPricesCount, color: '#1A56A0' },
          ]}
          assetValue="⭐⭐⭐⭐"
          useCase="식재료 시장가 통계 · 동의어 사전 학습 · 카테고리 분류"
        />

        <DataCard
          icon="📋" title="레시피"
          mainStat={stats.recipeCount}
          mainLabel="등록 레시피"
          subStats={[
            { label: '메뉴', val: stats.menuCount, color: '#18181B' },
            { label: '반제품', val: stats.semiCount, color: '#1A56A0' },
            { label: '평균 식재료수', val: stats.avgIngredients, color: '#374151' },
          ]}
          assetValue="🔒 영업비밀"
          useCase="레시피 raw 데이터는 익명 집계 X — 매장 식별 가능성. 사용량 통계만 추론으로 활용"
        />

        <DataCard
          icon="🏢" title="거래처 (partners)"
          mainStat={stats.partnerCount}
          mainLabel="등록 거래처"
          subStats={[
            { label: '사업자번호 보유', val: stats.partnersWithBizNum, color: '#18181B' },
            { label: '평균 거래 횟수', val: stats.avgPartnerTxns, color: '#374151' },
          ]}
          assetValue="⭐⭐⭐"
          useCase="공동구매 vendor 매칭 · 거래 네트워크 분석 · B2B 영업 데이터"
        />

        <DataCard
          icon="💼" title="인건비 / 직원"
          mainStat={stats.employeeCount}
          mainLabel="등록 직원"
          subStats={[
            { label: '이번달 스케줄 일수', val: stats.thisMonthSchedules, color: '#1A56A0' },
            { label: '활성 직원', val: stats.activeEmployees, color: '#18181B' },
          ]}
          assetValue="🔒 개인정보"
          useCase="개인정보 보호 — 직원 raw 데이터 X. 평균 인건비율만 익명 집계"
        />

        <DataCard
          icon="📊" title="손익 (profitLoss)"
          mainStat={stats.profitLossMonths}
          mainLabel="입력된 월"
          subStats={[
            { label: '평균 매출', val: fmtWon(stats.avgMonthRevenue), color: '#18181B', isString: true },
            { label: '평균 마진율', val: stats.avgMargin + '%', color: '#EA580C', isString: true },
          ]}
          assetValue="⭐⭐⭐"
          useCase="매장 운영 벤치마크 · 외식업 평균 통계 검증 · 손익 분포"
        />
      </div>

      {/* 자산화 로드맵 안내 */}
      <div style={{
        background: '#f0f9ff', borderRadius: 12,
        padding: '14px 18px',
        border: '1px solid #bfdbfe',
        fontSize: 12, color: '#0369a1', lineHeight: 1.7,
      }}>
        <div style={{ fontWeight: 800, marginBottom: 6 }}>🗺️ 데이터 자산화 로드맵</div>
        <ol style={{ paddingLeft: 18, margin: 0 }}>
          <li><strong>M3 — 약관·동의</strong>: 회원가입 시 "익명 데이터 활용 동의" 체크박스 추가 (선결조건)</li>
          <li><strong>M1 — 식재료 시장가 자산화</strong>: itemPrices를 anonymizedStats로 집계 → 평균/최저가 위젯</li>
          <li><strong>M2 — 메뉴 판매 트렌드</strong>: transactions의 lineItems(quantity 포함) 집계 → vendor 리포트</li>
          <li><strong>M4 — 공동구매 매칭</strong>: 매입 패턴 기반 그룹 추천 (실제 vendor 협업 후)</li>
        </ol>
      </div>

      {/* 데이터 정책 안내 */}
      <div style={{
        background: '#FAFAFA', borderRadius: 12,
        padding: '12px 16px',
        border: '1px solid #D4D4D8',
        fontSize: 11, color: '#78350f', lineHeight: 1.7,
      }}>
        <div style={{ fontWeight: 800, marginBottom: 4 }}>🔐 데이터 보호 원칙</div>
        ① 레시피 raw 데이터(ingredients 조합) 익명 집계 X — 영업비밀 보호 · 메뉴별 사용량은 추론만<br/>
        ② 직원·개인정보 익명 집계 X — 인건비 비율 같은 매장 단위 통계만<br/>
        ③ 매장 식별 정보 (uid, partnerName, 사업자번호) 집계 전 모두 제거<br/>
        ④ 사용자 명시적 동의 후에만 익명 통계로 활용 (옵트아웃 가능)
      </div>
    </div>
  );
}

// ── 데이터 카드 ──
function DataCard({ icon, title, mainStat, mainLabel, subStats = [], assetValue, useCase }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      padding: '14px 16px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
      border: '1px solid #e5e7eb',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#1a1a1a', flex: 1 }}>{title}</span>
        <span style={{ fontSize: 11 }}>{assetValue}</span>
      </div>

      <div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#18181B', lineHeight: 1.1 }}>
          {fmt(mainStat)}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{mainLabel}</div>
      </div>

      {subStats.length > 0 && (
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap',
          paddingTop: 6, borderTop: '1px dashed #e5e7eb',
        }}>
          {subStats.map((s, i) => (
            <div key={i} style={{
              flex: 1, minWidth: 70,
              fontSize: 10, color: '#6b7280',
            }}>
              <div style={{ marginBottom: 1 }}>{s.label}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>
                {s.isString ? s.val : fmt(s.val)}
              </div>
            </div>
          ))}
        </div>
      )}

      {useCase && (
        <div style={{
          fontSize: 10, color: '#6b7280', lineHeight: 1.5,
          background: '#f9fafb', padding: '6px 8px', borderRadius: 6,
          marginTop: 2,
        }}>
          💡 {useCase}
        </div>
      )}
    </div>
  );
}

// ── 데이터 수집 함수 ──
async function collectStats(uid) {
  const out = {
    transactionCount: 0, incomeCount: 0, expenseCount: 0,
    thisMonthTransactions: 0, thisMonthReceipts: 0,
    ocrReceiptCount: 0, totalLineItems: 0,
    menuSalesLineItems: 0, totalMenuQty: 0, avgMenuPrice: 0,
    priceCount: 0, priceMatched: 0, itemPricesCount: 0,
    purchaseLogCount: 0, purchaseLogItems: 0, purchaseLogTaxFree: 0,
    recipeCount: 0, menuCount: 0, semiCount: 0, avgIngredients: 0,
    partnerCount: 0, partnersWithBizNum: 0, avgPartnerTxns: 0,
    employeeCount: 0, activeEmployees: 0, thisMonthSchedules: 0,
    profitLossMonths: 0, avgMonthRevenue: 0, avgMargin: 0,
  };

  const monthKey = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();

  // ── transactions ──
  try {
    const txnsSnap = await getDocs(collection(db, 'users', uid, 'transactions'));
    out.transactionCount = txnsSnap.size;
    let menuQty = 0, menuRevenue = 0, menuLines = 0;
    txnsSnap.forEach(d => {
      const t = d.data();
      if (t.type === 'income') out.incomeCount++;
      else if (t.type === 'expense') out.expenseCount++;
      if (t.date && t.date.startsWith(monthKey)) out.thisMonthTransactions++;
      // OCR sourceReceipt 식별
      if (t.evidence?.sourceUrl || t.sourceReceiptId) out.ocrReceiptCount++;
      // 메뉴 판매 lineItems
      if (t.type === 'income' && Array.isArray(t.lineItems)) {
        t.lineItems.forEach(li => {
          if (li.menuId || li.name) {
            menuLines++;
            menuQty += Number(li.qty) || 0;
            menuRevenue += Number(li.total) || 0;
          }
        });
      }
      // 모든 lineItems
      if (Array.isArray(t.lineItems)) out.totalLineItems += t.lineItems.length;
    });
    out.menuSalesLineItems = menuLines;
    out.totalMenuQty = menuQty;
    out.avgMenuPrice = menuQty > 0 ? Math.round(menuRevenue / menuQty) : 0;
    out.thisMonthReceipts = Math.round(out.ocrReceiptCount * (out.thisMonthTransactions / Math.max(1, out.transactionCount)));
  } catch (e) { console.warn('transactions failed:', e); }

  // ── itemPrices ──
  try {
    const ipSnap = await getDocs(collection(db, 'users', uid, 'itemPrices'));
    out.itemPricesCount = ipSnap.size;
  } catch (e) { console.warn('itemPrices failed:', e); }

  // ── purchaseLog (간이영수증 메모) ──
  try {
    const plSnap = await getDocs(collection(db, 'users', uid, 'purchaseLog'));
    out.purchaseLogCount = plSnap.size;
    plSnap.forEach(d => {
      const pl = d.data();
      if (Array.isArray(pl.items)) {
        out.purchaseLogItems += pl.items.length;
        out.purchaseLogTaxFree += pl.items.filter(it => it.isTaxFree).length;
      }
    });
  } catch (e) { console.warn('purchaseLog failed:', e); }

  // ── partners ──
  try {
    const psSnap = await getDocs(collection(db, 'users', uid, 'partners'));
    out.partnerCount = psSnap.size;
    psSnap.forEach(d => {
      const p = d.data();
      if (p.bizNumber) out.partnersWithBizNum++;
    });
    out.avgPartnerTxns = out.partnerCount > 0 ? Math.round(out.expenseCount / out.partnerCount) : 0;
  } catch (e) { console.warn('partners failed:', e); }

  // ── 메인 user 문서 (categories, prices, employees, schedules, profitLoss) ──
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      // categories → 레시피
      const cats = data.categories || [];
      let menuCnt = 0, semiCnt = 0, totalIng = 0, ingCount = 0;
      cats.forEach(cat => {
        (cat.items || []).forEach(it => {
          if (it.type === 'menu') menuCnt++;
          if (it.type === 'semi') semiCnt++;
          if (Array.isArray(it.ingredients) && it.ingredients.length > 0) {
            totalIng += it.ingredients.length;
            ingCount++;
          }
        });
      });
      out.recipeCount = menuCnt + semiCnt;
      out.menuCount = menuCnt;
      out.semiCount = semiCnt;
      out.avgIngredients = ingCount > 0 ? Math.round(totalIng / ingCount) : 0;
      // prices
      const prices = data.prices || [];
      out.priceCount = prices.length;
      out.priceMatched = prices.filter(p => p.aiMatched || p.kamisName || p.naverName).length;
      // employees
      const emps = data.employees || [];
      out.employeeCount = emps.length;
      out.activeEmployees = emps.filter(e => e.active !== false).length;
      // schedules — 이번달
      const schedules = data.schedules || {};
      const thisMonth = schedules[monthKey] || {};
      out.thisMonthSchedules = Object.keys(thisMonth).length;
      // profitLoss
      const pl = data.profitLoss || {};
      const months = Object.keys(pl);
      out.profitLossMonths = months.length;
      if (months.length > 0) {
        let totalRev = 0, totalMargin = 0, marginCount = 0;
        months.forEach(m => {
          const r = pl[m].revenue || {};
          const c = pl[m].costs || {};
          const rev = (Number(r.card) || 0) + (Number(r.cash) || 0) + (Number(r.delivery) || 0);
          const cost = (Number(c.ingredients) || 0) + (Number(c.labor) || 0) + (Number(c.rent) || 0) + (Number(c.utilities) || 0) + (Number(c.other) || 0);
          totalRev += rev;
          if (rev > 0) {
            totalMargin += ((rev - cost) / rev) * 100;
            marginCount++;
          }
        });
        out.avgMonthRevenue = Math.round(totalRev / months.length);
        out.avgMargin = marginCount > 0 ? Math.round(totalMargin / marginCount) : 0;
      }
    }
  } catch (e) { console.warn('user doc failed:', e); }

  return out;
}
